import React, { useState, useEffect, useMemo } from "react";
import "./ReviewPayrollBatch.scss";
import {
  ScheduleJobList4,
  updateBatchStatus,
  ListTransFileRecs,
  UpdateTransFileRecs,
  sendEmail2,
  GetMerchantProfile
} from "../../util/API/batch";
import {
  CircularProgress,
  Box,
  Button,
  Modal,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import CustomAlert from "../UI/CustomAlert/CustomAlert";
import { useSelector } from "react-redux";
import PinPad from "../PinPad/PinPad";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { decodeHtmlEntities } from "../../util/utilfunctions/utilFunctions";

const PAYROLL_CATEGORY_ID = "33";
const INCORRECT_PIN_ERROR = "INCORRECT_PIN";

const STATUS_OPTIONS = [
  "Open",
  "Review",
  "Approved for Processing",
  "Rejected",
  "Completed",
  "Error",
  "Cancelled",
];

const STATUS_TO_CODE = {
  Open: "-2",
  "Review in Progress": "-1",
    "Approved for Processing": "1",
  "Approved for Processing": "0",
  Rejected: "5",
  Completed: "3",
  Error: "2",
  Cancelled: "4",
};

const getDisplayStatus = (code) => {
  const map = {
    "-2": "Open",
    "-1": "Review in Progress",
        "1": "Approved for Processing",
    "0": "Approved for Processing",

    "5": "Rejected",
    "3": "Completed",
    "2": "Error",
    "4": "Cancelled",
  };
  return map[code] || `Unknown (${code})`;
};

export default function ReviewPayrollBatch() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState(new Set());
  const [showPinPad, setShowPinPad] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingRecordSave, setPendingRecordSave] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
    header: "Success"
  });

  // Record editing state
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [editingRecId, setEditingRecId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // NEW STATES FOR SCHEDULED TIME EDITING
  const [editingScheduledId, setEditingScheduledId] = useState(null);
  const [tempScheduledValue, setTempScheduledValue] = useState("");
  const [pendingScheduledChange, setPendingScheduledChange] = useState(null);



  const merchantInfo = useSelector((state) => state.login.user);
  const currentUserId = merchantInfo?.id;
  const currentUsername = `${merchantInfo?.firstName || ""} ${merchantInfo?.lastName || ""}`.trim() || "Payroll Admin";




  const configVariables = useSelector((state) => state.config.baseConfig);
  const isDevEnv = configVariables?.devEnvironment;


  const emailRec = isDevEnv
    ? "no-reply@4pay.ca"
    : "kayon.stokes@ncsenterprises.com";

  console.log("emailrec0", emailRec);

  // NEW: Fetch company name via GetMerchantProfile
  const [companyName, setCompanyName] = useState("Unknown Company");

  useEffect(() => {
    const fetchCompanyName = async () => {
      if (currentUserId) {
        try {
          const res = await GetMerchantProfile("", currentUserId);
          console.log("GetMerchantProfile response:", JSON.stringify(res, null, 2));

          if (res?.callStatus === "1") {
            const businessname = res.payload?.Merchant?.businessname || "Unknown Company";
            setCompanyName(
              businessname
                .replace(/&amp;amp;/g, "&")
                .replace(/&amp;#039;/g, "'")
                .replace(/&amp;#39;/g, "'")
                .replace(/&amp;/g, "&")
                .replace(/&quot;/g, '"')
                .replace(/&#34;/g, "'")
                .replace(/&#039;/g, "'")
                .replace(/&#39;/g, "'")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .trim()
            );
          }
        } catch (err) {
          console.error("Failed to fetch merchant profile:", err);
        }
      }
    };

    fetchCompanyName();
  }, [currentUserId]);


  const cleanComment = (text) =>
    text
      ? text
        .replace(/&amp;amp;/g, "&")
        .replace(/&amp;#039;/g, "'")
        .replace(/&amp;#39;/g, "'")

        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&nbsp;/g, " ")
        .split("\n")
        .filter(Boolean)
        .join(" • ")
      : "—";
  const cleanFileName = (fileName) => {
    if (!fileName) return "—";

    return fileName
      .replace(/&amp;#039;/g, "'")   // ✅ decode encoded apostrophe first
      .replace(/&#039;/g, "'")        // ✅ decode numeric entity
      .replace(/&#39;/g, "'")         // ✅ decode short numeric entity
      .replace(/&amp;/g, "&")         // ✅ decode &amp;
      .replace(/&/g, "_and_")         // raw & → _and_
      .replace(/[()]/g, "")           // remove parentheses
      .replace(/[^a-zA-Z0-9\s'_.-]/g, "") // ✅ ' preserved
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .trim();
  };
  // ====================== TOTAL AMOUNT CALCULATION ======================
  const totalsByCurrency = useMemo(() => {
    const totals = {};

    records.forEach((rec) => {
      let amount = parseFloat(rec.amount) || 0;
      let currency = rec.currency || "CAD";

      // Use live edit values if this record is being edited
      if (rec.id === editingRecId) {
        amount = parseFloat(editForm.amount) || 0;
        currency = editForm.currency || "CAD";
      }

      if (!totals[currency]) totals[currency] = 0;
      totals[currency] += amount;
    });

    return totals;
  }, [records, editingRecId, editForm.amount, editForm.currency]);

  const grandTotal = Object.values(totalsByCurrency).reduce((sum, val) => sum + val, 0);
  const currencyList = Object.keys(totalsByCurrency);
  const isSingleCurrency = currencyList.length === 1;
  const mainCurrency = isSingleCurrency ? currencyList[0] : null;
  // =====================================================================

  const fetchAllPayrollBatches = async () => {
    setLoading(true);
    try {
      const response = await ScheduleJobList4({
        categoryid: PAYROLL_CATEGORY_ID,
        rows: 1000,
      });

      if (response.callStatus === "1") {
        let raw = response.payload?.record || [];
        if (!Array.isArray(raw)) raw = raw ? [raw] : [];

        const mapped = raw.map((item) => {
          const s = item.Scheduleinstance;
          const recordsMatch = s.notes?.match(/records:\s*(\d+)/i);
          const totalRecords = recordsMatch ? parseInt(recordsMatch[1], 10) : 0;

          // Clean notes: decode HTML entities
          const cleanNotes = s.notes
            ? decodeHtmlEntities(s.notes)

              .replace(/&amp;amp;/g, "&")
              .replace(/&amp;#039;/g, "'")
              .replace(/&amp;#39;/g, "'")

              .replace(/&amp;/g, "&")
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&lt;/g, "<")
              .replace(/&gt;/g, ">")
              .replace(/&nbsp;/g, " ")
              .split("\n")
              .filter(Boolean)
              .join(" • ")
            : "—";

          return {
            id: s.requestid,
            agentId: s.requestuserid,
            fileName: s.inputfilename,
            records: totalRecords,
            uploadedAt: s.requesttime || s.scheduletime || "—",
            notes: cleanNotes,
            status: getDisplayStatus(s.executionstatus),
            rawStatus: s.executionstatus,
            requestuserid: s.requestuserid, // ← Keep this for filtering
            totalAmount: parseFloat(s.amount) || 0,
            totalAmountDisplay: parseFloat(s.amount)
              ? parseFloat(s.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })
              : "—",
            scheduledTime: s.scheduletime || "—",
          };
        })
          // NEW FILTER: Only current user's batches
          .filter(batch => batch.requestuserid === currentUserId)
          .filter(batch =>
            batch.rawStatus === "-2" ||  // Open
            batch.rawStatus === "-1" ||  // Review
            batch.rawStatus === "5" ||    // Rejected
            batch.rawStatus === "4"
          );

        setBatches(mapped);
      } else {
        setBatches([]);
      }
    } catch (err) {
      console.error("Failed to load batches:", err);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPayrollBatches();
  }, []);

  const refreshBatches = async () => {
    setLoading(true);
    await fetchAllPayrollBatches();
    setLoading(false);
  };

  const openRecordEditor = async (batch) => {
    setSelectedBatch(batch);
    setRecords([]);
    setEditingRecId(null);
    setRecordsLoading(true);

    try {
      const res = await ListTransFileRecs(batch.id);

      if (res.callStatus !== "1") {
        setAlert({
          open: true,
          severity: "error",
          header: "Access Denied",
          message: "Cannot load records for this batch."
        });
        setRecordsLoading(false);
        return;
      }

      // FIXED: Handle both array and single object
      const raw = res.payload?.record || [];
      const rawArray = Array.isArray(raw) ? raw : [raw]; // ← This line fixes it

      if (rawArray.length === 0) {
        setAlert({
          open: true,
          severity: "info",
          header: "Empty Batch",
          message: "No records found in this batch."
        });
        setRecords([]);
        setRecordsLoading(false);
        return;
      }

      // Now safe to map
      const allRecords = rawArray
        .map(item => item.Transfilerecs)
        .filter(Boolean);

      if (allRecords.length === 0) {
        setAlert({
          open: true,
          severity: "warning",
          header: "No Data",
          message: "Records exist but structure is empty."
        });
        setRecords([]);
        setRecordsLoading(false);
        return;
      }

      const mapped = allRecords.map((r) => ({
        id: r.recid,
        recid: r.recid,
        requestid: r.requestid,
        firstname: r.firstname?.trim() || "",
        lastname: r.lastname?.trim() || "",
        email: r.email?.trim() || "",
        amount: r.amount || "",
        currency: r.currency || "",
        phoneno: r.phoneno || "",
        address1: r.address1 || "",
        address2: r.address2 || "",
        country: r.country || "",
        state: r.state || "",
        zipcode: r.zipcode || "",
        birthdate: r.birthdate === "0000-00-00" ? "" : r.birthdate,
        comment: decodeHtmlEntities(r.comment) || "",
        purposeofaccount: r.purposeofaccount || "",
        status: r.status === "0" ? "Pending" : r.status || "Pending",
        errormessage: r.errormessage || "",
      }));

      setRecords(mapped);
      console.log(`Loaded ${mapped.length} records successfully!`, mapped);
      setRecordsLoading(false);
    } catch (err) {
      console.error("Failed to load records:", err);
      setAlert({
        open: true,
        severity: "error",
        header: "Load Failed",
        message: "Error loading batch records."
      });
      setRecordsLoading(false);
    }
  };

  const startEdit = (record) => {
    setEditingRecId(record.id);
    setEditingRecId(record.id);
    setEditForm({
      ...record,
      comment: cleanComment(record.comment) === "—"
        ? ""
        : cleanComment(record.comment)
    });
  };

  const saveRecord = async () => {
    const statusCodeMap = { "Pending": "0", "CORRECTED": "1", "ERROR": "2" };

    const payloadToSend = {
      ...editForm,
      status: statusCodeMap[editForm.status] || editForm.status || "0",
    };

    const updatedRecords = records.map(r => (r.recid === editForm.recid ? { ...editForm } : r));
    const newTotal = updatedRecords.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    const amountChanged = newTotal !== selectedBatch?.totalAmount;

    if (amountChanged) {
      // Amount changed — PIN first, then both APIs fire in handlePinConfirm
      setPendingRecordSave({ payloadToSend, updatedRecords, newTotal });
      setShowPinPad(true);
    } else {
      // No amount change — save directly, no PIN needed
      try {
        const result = await UpdateTransFileRecs(payloadToSend);
        if (result.callStatus === "1") {
          setRecords(updatedRecords);
          setEditingRecId(null);
          setAlert({ open: true, severity: "success", header: "Saved!", message: "Record updated successfully." });
        } else {
          throw new Error();
        }
      } catch (err) {
        setAlert({ open: true, severity: "error", header: "Save Failed", message: "Could not update record." });
      }
    }
  };

  const requestStatusChange = (id, newStatus) => {
    const current = batches.find((b) => b.id === id)?.status;
    if (newStatus === current) return;
    setPendingAction({ id, newStatus });
    setShowPinPad(true);
  };

  // NEW - start inline edit for scheduled time
  const startEditScheduledTime = (batch) => {
    setEditingScheduledId(batch.id);
    if (batch.scheduledTime && batch.scheduledTime !== "—") {
      const [date, time] = batch.scheduledTime.split(" ");
      const timePart = time ? time.substring(0, 5) : "00:00";
      setTempScheduledValue(`${date}T${timePart}`);
    } else {
      setTempScheduledValue("");
    }
  };

  // NEW - trigger PIN prompt for scheduled time change
  const requestScheduledTimeChange = (batchId) => {
    if (!tempScheduledValue) return;
    const [date, time] = tempScheduledValue.split("T");
    const fullTime = `${date} ${time}:00`;
    setPendingScheduledChange({ batchId, newScheduledTime: fullTime });
    setShowPinPad(true);
  };

  const handlePinConfirm = async (event, pinArray) => {
    console.log('selectedBatch--------------<', selectedBatch)
    if ((!pendingAction && !pendingScheduledChange && !pendingRecordSave) || !Array.isArray(pinArray)) {
      setShowPinPad(false);
      setPendingAction(null);
      setPendingScheduledChange(null);
      setPendingRecordSave(null);
      return;
    }

    const PIN = pinArray.join("");
    // ── RECORD AMOUNT CHANGE ──
    if (pendingRecordSave) {
      const { payloadToSend, updatedRecords, newTotal } = pendingRecordSave;
      let isPinError = false;

      try {
        const recordResult = await UpdateTransFileRecs(payloadToSend);
        if (recordResult.callStatus !== "1") throw new Error("Failed to save record");
        const batchResult = await updateBatchStatus({
          requestid: selectedBatch.id,
          executionstatus: selectedBatch.rawStatus,
          notes: selectedBatch.notes || "",
          pin: PIN,
          requestuserid: currentUserId,
          amount: newTotal,
          uploadedby:selectedBatch.agentId,
          approvedby: 0,
          lastuserid:currentUserId,
          scheduletime: selectedBatch.scheduledTime !== "—" ? selectedBatch.scheduledTime : undefined,
        });

        if (
          batchResult.callStatus !== "1" &&
          (
            batchResult?.payload?.msg?.toLowerCase().includes("pin") ||
            batchResult?.payload?.msg?.toLowerCase().includes("invalid pin") ||
            batchResult?.payload?.msg?.toLowerCase().includes("incorrect pin")
          )
        ) {
          throw new Error(INCORRECT_PIN_ERROR);
        }

        if (batchResult.callStatus !== "1") throw new Error("Failed to sync batch total");

        setRecords(updatedRecords);
        setEditingRecId(null);

        setBatches(prev =>
          prev.map(b =>
            b.id === selectedBatch.id
              ? {
                ...b,
                totalAmount: newTotal,
                totalAmountDisplay: newTotal.toLocaleString("en-US", { minimumFractionDigits: 2 }),
              }
              : b
          )
        );
        setSelectedBatch(prev => ({ ...prev, totalAmount: newTotal }));
        setAlert({ open: true, severity: "success", header: "Saved!", message: "Record and batch total updated successfully." });

      } catch (error) {
        if (error.message === INCORRECT_PIN_ERROR) {
          isPinError = true;
          setAlert({ open: true, severity: "error", header: "Incorrect PIN", message: "The PIN you entered is wrong. Please try again." });
          setShowPinPad(true);
          return;
        }
        setAlert({ open: true, severity: "error", header: "Failed", message: error.message || "Could not save record." });
      } finally {
        if (!isPinError) {
          setShowPinPad(false);
          setPendingRecordSave(null);
        }
      }
      return; // ← CRITICAL: prevent falling through to other branches
    }

    // ── STATUS CHANGE ──
    else if (pendingAction) {
      const { id, newStatus } = pendingAction;
      const original = batches.find((b) => b.id === id);

      setBatches((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status: getDisplayStatus(newStatus) } : b))
      );
      setSavingIds((prev) => new Set(prev).add(id));

      let isPinError = false;

      try {
        const result = await updateBatchStatus({
          requestid: id,
          executionstatus: newStatus,
          notes: "",
          pin: PIN,
          requestuserid: currentUserId,
          amount: original?.totalAmount || 0,
          uploadedby:original?.agentId,
          approvedby: 0,
          lastuserid:currentUserId,
        });

        if (
          result.callStatus !== "1" &&
          (
            result?.payload?.msg?.toLowerCase().includes("pin") ||
            result?.payload?.msg?.toLowerCase().includes("invalid pin") ||
            result?.payload?.msg?.toLowerCase().includes("incorrect pin") ||
            (result?.payload?.callStatus === "0" && result?.payload?.msg?.includes("authentication"))
          )
        ) {
          throw new Error(INCORRECT_PIN_ERROR);
        }

        if (result.callStatus !== "1") throw new Error("Failed to update status");

        if (newStatus === "-1") {
          const batch = batches.find((b) => b.id === id);
          const subject = `Payroll Batch #${id} Ready for Review`;
          const emailText = `PAYROLL BATCH READY FOR REVIEW\n\nBatch ID: #${id}\nFile: ${getDisplayFileName(batch.fileName)}\nRecords: ${batch.records}\nSubmitted By: ${currentUsername} (${companyName})\n\nPlease log in and review the batch at your earliest convenience.\n\n— NCS Payroll Service`;
          try {
            const emailRecipient = isDevEnv
              ? "no-reply@4pay.ca"
              : "kayon.stokes@ncsenterprises.com";
            await sendEmail2({ destinationEmailAccount: emailRecipient, subject, emailText });
          } catch (err) {
            console.warn("Email failed (non-blocking):", err);
          }
        }

        await refreshBatches();
        setAlert({
          open: true,
          severity: "success",
          header: "Success",
          message: `Batch #${id} → ${getDisplayStatus(newStatus)}${newStatus === "-1" ? " — Email sent!" : ""}`,
        });

      } catch (error) {
        if (error.message === INCORRECT_PIN_ERROR) {
          isPinError = true;
          setAlert({ open: true, severity: "error", header: "Incorrect PIN", message: "The PIN you entered is wrong. Please try again." });
          setBatches((prev) => prev.map((b) => (b.id === id ? original : b)));
          setShowPinPad(true);
          return;
        }
        setAlert({ open: true, severity: "error", header: "Failed", message: "Status update failed." });
        setBatches((prev) => prev.map((b) => (b.id === id ? original : b)));
      } finally {
        setSavingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
        if (!isPinError) {
          setShowPinPad(false);
          setPendingAction(null);
        }
      }
    }

    // ── SCHEDULED TIME CHANGE ──
    else if (pendingScheduledChange) {
      const { batchId, newScheduledTime } = pendingScheduledChange;
      const batch = batches.find(b => b.id === batchId);
      if (!batch) return;

      setSavingIds(prev => new Set([...prev, batchId]));
      let isPinError = false;

      try {
        const result = await updateBatchStatus({
          requestid: batchId,
          executionstatus: batch.rawStatus,
          notes: "",
          pin: PIN,
          requestuserid: currentUserId,
          amount: batch.totalAmount || 0,
          uploadedby: batch?.agentId,
          approvedby: 0,
          lastuserid:currentUserId,
          scheduletime: newScheduledTime
        });

        if (
          result.callStatus !== "1" &&
          (
            result?.payload?.msg?.toLowerCase().includes("pin") ||
            result?.payload?.msg?.toLowerCase().includes("invalid pin") ||
            result?.payload?.msg?.toLowerCase().includes("incorrect pin") ||
            (result?.payload?.callStatus === "0" && result?.payload?.msg?.includes("authentication"))
          )
        ) {
          throw new Error(INCORRECT_PIN_ERROR);
        }

        if (result.callStatus !== "1") throw new Error("Failed to update scheduled time");

        await refreshBatches();
        setAlert({ open: true, severity: "success", header: "Success", message: `Scheduled time for batch #${batchId} updated` });
        setEditingScheduledId(null);
        setTempScheduledValue("");

      } catch (error) {
        if (error.message === INCORRECT_PIN_ERROR) {
          isPinError = true;
          setAlert({ open: true, severity: "error", header: "Incorrect PIN", message: "The PIN you entered is wrong. Please try again." });
          setShowPinPad(true);
          return;
        }
        setAlert({ open: true, severity: "error", header: "Failed", message: "Could not update scheduled time." });
      } finally {
        setSavingIds(prev => { const s = new Set(prev); s.delete(batchId); return s; });
        if (!isPinError) {
          setShowPinPad(false);
          setPendingScheduledChange(null);
        }
      }
    }
  };

  const getStatusColor = (status) => ({
    Open: "#e2e3e5",
    "Review in Progress": "#fff3cd",
    "Approved for Processing": "#d4edda",
    Rejected: "#f8d7da",
    Completed: "#d1ecf1",
    Error: "#f5c6cb",
    Cancelled: "#d6d8db",
  }[status] || "#f8f9fa");

  const getStatusTextColor = (status) => ({
    Open: "#383d41",
    "Review in Progress": "#856404",
    "Approved for Processing": "#155724",
    Rejected: "#721c24",
    Completed: "#0c5460",
    Error: "#721c24",
    Cancelled: "#495057",
  }[status] || "#333");

  const handleDownloadCSV = async (batchId, fileName) => {
    try {
      const res = await ListTransFileRecs(batchId);

      if (res.callStatus !== "1") {
        setAlert({
          open: true,
          severity: "error",
          header: "Download Failed",
          message: "Could not load records for download."
        });
        return;
      }

      const rawArray = res.payload?.record || [];
      const recordsArray = Array.isArray(rawArray) ? rawArray : rawArray ? [rawArray] : [];
      const allRecords = recordsArray.map(item => item.Transfilerecs).filter(Boolean);

      if (allRecords.length === 0) {
        setAlert({
          open: true,
          severity: "info",
          header: "No Data",
          message: "No records to download."
        });
        return;
      }

      const headers = [
        "First Name", "Last Name", "Email", "Phone", "Amount", "Currency",
        "Address Line 1", "Address Line 2", "Country", "State", "Zip Code",
        "Date of Birth", "Comment", "Purpose of Account"
      ];

      const rows = allRecords.map(r => [
        r.firstname || "",
        r.lastname || "",
        r.email || "",
        r.phoneno || "",
        r.amount || "",
        r.currency || "",
        r.address1 || "",
        r.address2 || "",
        r.country || "",
        r.state || "",
        r.zipcode || "",
        r.birthdate || "",
        decodeHtmlEntities(r.comment) || "",
        r.purposeofaccount || ""
      ]);

      const csv = [headers.join(","), ...rows.map(row => row.map(cell => `"${cell}"`).join(","))].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${getDisplayFileName(fileName).replace(".csv", "")}_records.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed:", err);
      setAlert({
        open: true,
        severity: "error",
        header: "Download Failed",
        message: "Error generating CSV file."
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }
  const getDisplayFileName = (fileName) => {
  if (!fileName) return "—";
  const fixedFileName = fileName.replace(/BATCH_-/i, "BATCH-");
  const regexPattern = companyName
    .replace(/[^a-zA-Z0-9'\s]/g, "_+")
    .replace(/'/g, "_")
    .replace(/\s+/g, "_+");

  const displayCompany = companyName
    .replace(/[^a-zA-Z0-9'\s]/g, "_")
    .replace(/\s+/g, "_");

  try {
    const regex = new RegExp(regexPattern, "i");
    const restored = fixedFileName.replace(regex, displayCompany);
    return cleanFileName(restored);
  } catch (e) {
    return cleanFileName(fixedFileName);
  }
};
  return (
    <div className="review-payroll-container">
      <div className="review-header">
        <h2>Payroll Batch Management </h2>
        <p>Review, edit individual records</p>
      </div>

      <div className="review-table-wrapper">
        {batches.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: "#666" }}>
            No payroll batches found.
          </Box>
        ) : (
          <table className="review-table">
            <thead>
              <tr>
                <th>Batch ID</th>
                <th>Agent ID</th>
                <th>File</th>
                <th>Total Amount</th>
                {/* <th>Records</th> */}
                <th>Uploaded</th>
                <th>Scheduled Time</th>
                <th>Notes</th>

                <th>Actions</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch.id}>
                  <td><strong>{batch.id}</strong></td>
                  <td>{batch.agentId}</td>
                  <td>  <code>
                    {getDisplayFileName(batch.fileName)}
                  </code></td>
                  <td style={{ textAlign: "right", fontWeight: "500", color: "#1976d2" }}>
                    {batch.totalAmountDisplay}
                  </td>
                  {/* <td>{batch.records.toLocaleString()}</td> */}
                  <td>{batch.uploadedAt}</td>

                  {/* MODIFIED: Scheduled Time column with inline editing */}
                  <td>
                    {editingScheduledId === batch.id ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <TextField
                          type="datetime-local"
                          size="small"
                          value={tempScheduledValue}
                          onChange={(e) => setTempScheduledValue(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 180 }}
                        />
                        <IconButton size="small" color="success" onClick={() => requestScheduledTimeChange(batch.id)}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => { setEditingScheduledId(null); setTempScheduledValue(""); }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {batch.scheduledTime}
                        {(batch.rawStatus === "-2" || batch.rawStatus === "5") && (
                          <Tooltip title="Edit scheduled time">
                            <IconButton size="small" onClick={() => startEditScheduledTime(batch)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </td>

                  <td style={{ fontSize: "0.85rem", maxWidth: "340px" }}>
                    {batch.notes}
                  </td>
                  {/* <td>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(batch.status),
                        color: getStatusTextColor(batch.status),
                      }}
                    >
                      {batch.status}
                    </span>
                  </td> */}
                  <td>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => openRecordEditor(batch)}
                        sx={{ mr: 1, backgroundColor: "#5523D2" }}
                      >
                        {batch.status === "Open" || batch.status === "Rejected" ? "Edit Records" : "View Records"}
                      </Button>

                      <Tooltip title="Download CSV">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadCSV(batch.id, batch.fileName)}
                          sx={{ color: "#145388" }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </td>

                  <td>
                    {savingIds.has(batch.id) ? (
                      <CircularProgress size={20} />
                    ) : (batch.rawStatus === "-1" || batch.rawStatus === "4") ? (  // ← If status is Review → no dropdown, just show text
                      <span style={{ fontWeight: "bold", color: "#856404" }}>
                        {batch.status}
                      </span>
                    ) : (
                      <select
                        value={batch.rawStatus}
                        onChange={(e) => requestStatusChange(batch.id, e.target.value)}
                        className="status-select"
                      >
                        <option value={batch.rawStatus} disabled hidden>
                          {batch.status}
                        </option>

                        {/* Show "Open" only if current is NOT Open */}
                        {batch.rawStatus !== "-2" && batch.rawStatus !== "-1" && batch.rawStatus !== "5" && (
                          <option value="-2">Open</option>
                        )}

                        {/* Show "Send For Review" only if current is NOT Review */}
                        {batch.rawStatus !== "-1" && (
                          <option value="-1">Send for NCS Approval</option>
                        )}

                        {batch.rawStatus !== "-1" && batch.rawStatus !== "-2" && batch.rawStatus !== "0" && batch.rawStatus !== "3" && batch.rawStatus !== "4" && batch.rawStatus !== "2" && (
                          <option value="4">Cancel</option>
                        )}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* EDIT MODAL (only for editable batches) */}
      <Modal open={!!selectedBatch && (selectedBatch.status === "Open" || selectedBatch.status === "Rejected")} onClose={() => setSelectedBatch(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            maxWidth: 1650,
            maxHeight: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            overflow: "auto",
          }}
        >
          {selectedBatch && (
            <>
              <Typography variant="h5" gutterBottom>
                
                Editing Batch #{selectedBatch.id} — {getDisplayFileName(selectedBatch.fileName)}
              </Typography>

              <Typography variant="body1" sx={{ mb: 3 }}>
                <strong>{records.length}</strong> record{records.length !== 1 && "s"} • Click Edit to modify
              </Typography>

              {recordsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                  <CircularProgress size={48} />
                  <Typography sx={{ ml: 2, color: "#666" }}>Loading records...</Typography>
                </Box>
              ) : records.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8, color: "#999" }}>
                  No records found in this batch.
                </Box>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                    <thead>
                      <tr style={{ background: "#5523D2", color: "white" }}>
                        <th style={{ padding: "12px", textAlign: "left" }}>First Name</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Last Name</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Email</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Phone</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Amount</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Currency</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Address</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Country</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>DOB</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Comment</th>
                        <th style={{ padding: "12px", textAlign: "center" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((rec) => (
                        <tr key={rec.id} style={{ borderBottom: "1px solid #eee" }}>
                          {editingRecId === rec.id ? (
                            // Editable row
                            <>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  value={editForm.firstname || ""}
                                  onChange={(e) => setEditForm({ ...editForm, firstname: e.target.value })}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  value={editForm.lastname || ""}
                                  onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  value={editForm.email || ""}
                                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  value={editForm.phoneno || ""}
                                  onChange={(e) => setEditForm({ ...editForm, phoneno: e.target.value })}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  step="0.01"
                                  value={editForm.amount || ""}
                                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px" }}>
                                <Select
                                  size="small"
                                  value={editForm.currency || "CAD"}
                                  onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                                  fullWidth
                                >
                                  <MenuItem value="CAD">CAD</MenuItem>
                                  <MenuItem value="USD">USD</MenuItem>
                                  <MenuItem value="EUR">EUR</MenuItem>
                                </Select>
                              </td>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  value={`${editForm.address1 || ""} ${editForm.address2 || ""}`.trim()}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const parts = val.split(" ");
                                    setEditForm({
                                      ...editForm,
                                      address1: parts.slice(0, 8).join(" "),
                                      address2: parts.slice(8).join(" "),
                                    });
                                  }}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  value={editForm.country || ""}
                                  onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  type="date"
                                  value={editForm.birthdate || ""}
                                  onChange={(e) => setEditForm({ ...editForm, birthdate: e.target.value })}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px" }}>
                                <TextField
                                  size="small"
                                  value={editForm.comment || ""}
                                  onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
                                  fullWidth
                                />
                              </td>
                              <td style={{ padding: "10px", textAlign: "center" }}>
                                <Button size="small" variant="contained" color="success" onClick={saveRecord}>
                                  Save
                                </Button>
                                <Button size="small" onClick={() => setEditingRecId(null)} sx={{ ml: 1 }}>
                                  Cancel
                                </Button>
                              </td>
                            </>
                          ) : (
                            // View-only row in edit modal
                            <>
                              <td style={{ padding: "10px" }}>{rec.firstname || "—"}</td>
                              <td style={{ padding: "10px" }}>{rec.lastname || "—"}</td>
                              <td style={{ padding: "10px" }}>{rec.email || "—"}</td>
                              <td style={{ padding: "10px" }}>{rec.phoneno || "—"}</td>
                              <td style={{ padding: "10px", fontWeight: "bold" }}>
                                {rec.amount} {rec.currency}
                              </td>
                              <td style={{ padding: "10px" }}>{rec.currency || "—"}</td>
                              <td style={{ padding: "10px" }}>
                                {rec.address1} {rec.address2}
                              </td>
                              <td style={{ padding: "10px" }}>{rec.country || "—"}</td>
                              <td style={{ padding: "10px" }}>{rec.birthdate || "—"}</td>
                              <td style={{ padding: "10px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }} title={rec.comment}>
                                {cleanComment(rec.comment)}
                              </td>
                              <td style={{ padding: "10px" }}>
                                <Button size="small" variant="outlined" onClick={() => startEdit(rec)}>
                                  Edit
                                </Button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* TOTAL AMOUNT SECTION */}
                  <Box sx={{ mt: 3, p: 3, backgroundColor: "#f0f4ff", borderRadius: 2, border: "1px solid #c5d4ff", textAlign: "right" }}>
                    <Typography variant="h6" fontWeight="bold" color="#1a237e">Total Amount</Typography>

                    {isSingleCurrency ? (
                      <Typography variant="h4" fontWeight="bold" color="#1976d2" sx={{ mt: 1 }}>
                        {grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {mainCurrency}
                      </Typography>
                    ) : (
                      <>
                        {Object.entries(totalsByCurrency).map(([curr, total]) => (
                          <Typography key={curr} variant="h6" sx={{ mt: 0.5 }}>
                            {total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {curr}
                          </Typography>
                        ))}
                      </>
                    )}
                  </Box>
                </div>
              )}

              <Box sx={{ mt: 4, textAlign: "right" }}>
                <Button variant="contained" color="error" onClick={() => setSelectedBatch(null)}>
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* VIEW-ONLY MODAL (for non-editable statuses) */}
      <Modal open={!!selectedBatch && (selectedBatch.status !== "Open" && selectedBatch.status !== "Rejected")} onClose={() => setSelectedBatch(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            maxWidth: 1650,
            maxHeight: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            overflow: "auto",
          }}
        >
          {selectedBatch && (
            <>
              <Typography variant="h5" gutterBottom>
                Viewing Batch #{selectedBatch.id} — {getDisplayFileName(selectedBatch.fileName)}
              </Typography>

              <Typography variant="body1" sx={{ mb: 3, color: "#856404", fontWeight: "bold" }}>
                View-only mode (Status: {selectedBatch.status}) — Editing is not allowed.
              </Typography>

              {recordsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                  <CircularProgress size={48} />
                  <Typography sx={{ ml: 2, color: "#666" }}>Loading records...</Typography>
                </Box>
              ) : records.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8, color: "#999" }}>
                  No records found in this batch.
                </Box>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                    <thead>
                      <tr style={{ background: "#5523D2", color: "white" }}>
                        <th style={{ padding: "12px", textAlign: "left" }}>First Name</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Last Name</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Email</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Phone</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Amount</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Currency</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Address</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Country</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>DOB</th>
                        <th style={{ padding: "12px", textAlign: "left" }}>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((rec) => (
                        <tr key={rec.id} style={{ borderBottom: "1px solid #eee" }}>
                          <td style={{ padding: "10px" }}>{rec.firstname || "—"}</td>
                          <td style={{ padding: "10px" }}>{rec.lastname || "—"}</td>
                          <td style={{ padding: "10px" }}>{rec.email || "—"}</td>
                          <td style={{ padding: "10px" }}>{rec.phoneno || "—"}</td>
                          <td style={{ padding: "10px", fontWeight: "bold" }}>
                            {rec.amount} {rec.currency}
                          </td>
                          <td style={{ padding: "10px" }}>{rec.currency || "—"}</td>
                          <td style={{ padding: "10px" }}>
                            {rec.address1} {rec.address2}
                          </td>
                          <td style={{ padding: "10px" }}>{rec.country || "—"}</td>
                          <td style={{ padding: "10px" }}>{rec.birthdate || "—"}</td>
                          <td style={{ padding: "10px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }} title={rec.comment}>
                            {rec.comment || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* TOTAL AMOUNT SECTION */}
                  <Box sx={{ mt: 3, p: 3, backgroundColor: "#f0f4ff", borderRadius: 2, border: "1px solid #c5d4ff", textAlign: "right" }}>
                    <Typography variant="h6" fontWeight="bold" color="#1a237e">Total Amount</Typography>

                    {isSingleCurrency ? (
                      <Typography variant="h4" fontWeight="bold" color="#1976d2" sx={{ mt: 1 }}>
                        {grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {mainCurrency}
                      </Typography>
                    ) : (
                      <>
                        {Object.entries(totalsByCurrency).map(([curr, total]) => (
                          <Typography key={curr} variant="h6" sx={{ mt: 0.5 }}>
                            {total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {curr}
                          </Typography>
                        ))}
                      </>
                    )}
                  </Box>
                </div>
              )}

              <Box sx={{ mt: 4, textAlign: "right" }}>
                <Button variant="contained" color="error" onClick={() => setSelectedBatch(null)}>
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* PIN PAD */}
      {showPinPad && (
        <PinPad
          show={showPinPad}
          handleConfirm={handlePinConfirm}
          onClick={() => {
            setShowPinPad(false);
            setPendingAction(null);
            setPendingScheduledChange(null);
            setPendingRecordSave(null);
          }}
        />
      )}

      <CustomAlert
        severity={alert.severity}
        show={alert.open}
        header={alert.header}
        message={alert.message}
        onClick={() => setAlert({ ...alert, open: false })}
        autoclose={alert.severity === "success"}
      />
    </div>
  );
}